if (!customElements.get('botslab-preorder-video')) {
  customElements.define('botslab-preorder-video', class extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.mobileQuery = window.matchMedia('(max-width: 767px)');
      this.visibleVideos = new Map();
      this.userPaused = new WeakSet();
      this.pendingPlayback = new WeakSet();
      this.canPlay = (video) => {
        const device = this.mobileQuery.matches ? 'mobile' : 'desktop';
        return this.isConnected && !document.hidden && this.visibleVideos.get(video)
          && !!video.closest(`.bpo-footage__media--${device}`);
      };
      this.syncPlayback = () => {
        this.visibleVideos.forEach((visible, video) => {
          if (!this.canPlay(video)) {
            video.pause();
          } else if (video.paused && !this.userPaused.has(video) && !this.pendingPlayback.has(video)) {
            this.pendingPlayback.add(video);
            // Muted inline playback starts only once the video enters the viewport.
            video.play().then(() => {
              if (!this.canPlay(video)) video.pause();
            }).catch(() => {
              // Keep manual controls available if the browser blocks autoplay.
            }).finally(() => this.pendingPlayback.delete(video));
          }
        });
      };
      this.pauseHiddenVideo = () => {
        const hiddenDevice = this.mobileQuery.matches ? 'desktop' : 'mobile';
        this.querySelectorAll(`.bpo-footage__media--${hiddenDevice} video`).forEach((video) => video.pause());
      };
      this.mobileQuery.addEventListener('change', this.syncPlayback);
      document.addEventListener('visibilitychange', this.syncPlayback, options);
      this.addEventListener('play', this.pauseHiddenVideo, true);
      this.pauseHiddenVideo();
      this.querySelectorAll('video').forEach((video) => {
        this.visibleVideos.set(video, false);
        video.addEventListener('pause', () => {
          if (this.canPlay(video)) this.userPaused.add(video);
        }, options);
        video.addEventListener('play', () => this.userPaused.delete(video), options);
      });
      this.querySelectorAll('.bpo-footage__media--mobile').forEach((media) => {
        const video = media.querySelector('video');
        const controls = media.querySelector('.bpo-footage__controls');
        if (!video || !controls) return;
        const toggle = controls.querySelector('[data-footage-toggle]');
        const sound = controls.querySelector('[data-footage-sound]');
        const fullscreen = controls.querySelector('[data-footage-fullscreen]');
        const update = () => {
          media.classList.toggle('is-playing', !video.paused && !video.ended);
          media.classList.toggle('is-muted', video.muted);
          toggle.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
          sound.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
        };
        toggle.addEventListener('click', () => {
          if (video.paused) {
            // Remove the central overlay on the tap, including while buffering.
            media.classList.add('is-playing');
            toggle.setAttribute('aria-label', 'Pause video');
            video.play().catch(update);
          } else {
            video.pause();
          }
        }, options);
        sound.addEventListener('click', () => { video.muted = !video.muted; }, options);
        fullscreen.addEventListener('click', () => {
          if (video.webkitEnterFullscreen) {
            video.webkitEnterFullscreen();
          } else if (media.requestFullscreen) {
            media.requestFullscreen().catch(() => {});
          }
        }, options);
        fullscreen.hidden = !video.webkitEnterFullscreen && !media.requestFullscreen;
        ['play', 'pause', 'ended', 'volumechange', 'error'].forEach((event) => {
          video.addEventListener(event, update, options);
        });
        video.controls = false;
        controls.hidden = false;
        update();
      });
      if ('IntersectionObserver' in window) {
        this.viewportObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const video = entry.target.querySelector('video');
            const visible = entry.isIntersecting && entry.intersectionRatio >= 0.15;
            this.visibleVideos.set(video, visible);
            if (!visible) this.userPaused.delete(video);
          });
          this.syncPlayback();
        }, { threshold: [0, 0.15] });
        this.querySelectorAll('.bpo-footage__media').forEach((media) => {
          if (media.querySelector('video')) this.viewportObserver.observe(media);
        });
      }
    }

    disconnectedCallback() {
      this.events.abort();
      this.viewportObserver?.disconnect();
      this.mobileQuery.removeEventListener('change', this.syncPlayback);
      this.removeEventListener('play', this.pauseHiddenVideo, true);
      this.querySelectorAll('video').forEach((video) => video.pause());
    }
  });
}
